import type {UploadResponse} from "../core/transfer/TransferService";

/** The deliberately small service surface the sender flow needs after recipient delivery fails. */
export interface PendingDeliverySource {
	readonly hasPendingDelivery: boolean;
	resumePendingDelivery(): Promise<UploadResponse>;
	discardPendingDelivery(): void;
}

export type DeliveryResumeState = "idle" | "pending" | "retrying";

export interface DeliveryRetryAction {
	/** Only the first concurrent caller owns UI transitions and notifications. */
	readonly started: boolean;
	/** All concurrent callers observe the same opaque service operation. */
	readonly result: Promise<UploadResponse>;
}

/**
 * Framework-free sender-flow orchestration for a completed upload whose recipient delivery is parked.
 * The controller never receives files, passwords, request settings, recipients, or a transfer id.
 */
export class DeliveryResumeOrchestrator {
	private currentState: DeliveryResumeState = "idle";
	private retryInFlight: Promise<UploadResponse> | null = null;
	private confirmationOpen = false;

	constructor(private readonly source: PendingDeliverySource) {}

	get state(): DeliveryResumeState {
		return this.currentState;
	}

	get startOverConfirmationOpen(): boolean {
		return this.confirmationOpen;
	}

	/** Captures only the service's opaque pending signal after the original upload promise rejects. */
	captureFailure(): DeliveryResumeState {
		this.confirmationOpen = false;
		this.currentState = this.source.hasPendingDelivery ? "pending" : "idle";
		return this.currentState;
	}

	/** Shares concurrent retry actions while preserving pending state after any failed resume. */
	retry(): DeliveryRetryAction {
		if (this.retryInFlight !== null) {
			return {started: false, result: this.retryInFlight};
		}
		if (this.currentState !== "pending" || !this.source.hasPendingDelivery) {
			return {
				started: false,
				result: Promise.reject(new Error("Delivery retry requested without a pending delivery")),
			};
		}
		this.confirmationOpen = false;
		this.currentState = "retrying";
		const run = this.source.resumePendingDelivery().then(
			response => {
				this.currentState = "idle";
				return response;
			},
			error => {
				this.currentState = this.source.hasPendingDelivery ? "pending" : "idle";
				throw error;
			},
		);
		this.retryInFlight = run;
		const clear = () => {
			if (this.retryInFlight === run) {
				this.retryInFlight = null;
			}
		};
		void run.then(clear, clear);
		return {started: true, result: run};
	}

	/** Opens the explicit destructive-action gate only while delivery is pending and idle. */
	requestStartOver(): boolean {
		if (this.currentState !== "pending" || this.retryInFlight !== null || !this.source.hasPendingDelivery) {
			return false;
		}
		this.confirmationOpen = true;
		return true;
	}

	/** Closes the gate without changing the parked delivery or its retryable state. */
	cancelStartOver(): DeliveryResumeState {
		this.confirmationOpen = false;
		return this.currentState;
	}

	/** Discards only after the gate was explicitly opened and delivery is still safely pending. */
	confirmStartOver(): DeliveryResumeState {
		if (!this.confirmationOpen || this.currentState !== "pending" || this.retryInFlight !== null) {
			return this.currentState;
		}
		this.confirmationOpen = false;
		this.source.discardPendingDelivery();
		this.currentState = "idle";
		return this.currentState;
	}
}
